<script setup lang="ts">
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()

function getIcon(index: StringifiedInt<IntRange<0, 5>>) {
  return match(index)
    .with('0', () => 'bullseye-outline' satisfies IconName)
    .with('1', () => 'rocket-outline' satisfies IconName)
    .with('2', () => 'heart-outline' satisfies IconName)
    .with('3', () => 'sparks-outline' satisfies IconName)
    .with('4', () => 'leader-group' satisfies IconName)
    .exhaustive() as IconName
}
</script>

<template>
  <div class="flex w-full flex-col items-center justify-center">
    <div class="mb-6 text-3xl font-normal md:mb-14">
      {{ t("careers.section.how_we_work.header") }}
    </div>

    <div class="grid grid-cols-3 gap-5">
      <img
        class="hidden aspect-[9/16] size-full rounded-3xl object-cover lg:block"
        :src="`${S3_ASSETS_URL}/careers/hero-03.jpg`"
      >

      <div class="col-span-3 grid md:gap-5 lg:col-span-2 lg:grid-cols-2">
        <div
          v-for="(item, itemIndex) in tm('careers.section.how_we_work.cards')"
          :key="itemIndex"
          :class="[
            'relative col-span-2 mb-2 !flex grid flex-col place-content-center place-items-center items-start gap-1 text-balance rounded-2xl border border-neutral-200 bg-white p-4 pl-[60px] text-center shadow shadow-black/5 md:col-span-1 md:mb-0 md:items-center md:gap-5 md:pl-4',
            {
              'row-span-2': ['3', '4'].includes(itemIndex),
            },
            {
              'order-2 md:order-1': itemIndex === '0',
              'order-4 md:order-2': itemIndex === '1',
              'order-1 md:order-3': itemIndex === '2',
              'order-5 md:order-4': itemIndex === '3',
              'order-3 md:order-5': itemIndex === '4',
            },
          ]"
        >
          <div
            class="-ml-[40px] flex flex-row items-center text-left md:ml-0 md:flex-col md:text-center"
          >
            <UiIcon
              :name="getIcon(itemIndex)"
              class="mx-auto mt-1 block w-fit pr-2 text-3xl text-orange-500 md:mt-0 md:pr-0"
            />

            <h4 class="text-xl font-medium md:text-2xl">
              {{ rt(item.header) }}
            </h4>
          </div>

          <i18n-t
            :keypath="`careers.section.how_we_work.cards.${itemIndex}.description.base`"
            tag="p"
            class="text-left text-sm text-neutral-600 md:text-center md:text-base"
          >
            <template #placeholder>
              <strong class="font-normal md:font-bold">{{
                rt(item.description.placeholder)
              }}</strong>
            </template>
          </i18n-t>
        </div>

        <UiButton as-child>
          <a
            href="/careers-listing"
            rel="nofollow noopener"
            class="group col-start-2 hidden size-full gap-x-6 !rounded-2xl !p-6 text-xl !font-bold lg:flex"
            :style="{
              order: Object.keys(tm('careers.section.how_we_work.cards'))
                .length,
            }"
          >
            {{ t("careers.section.how_we_work.action") }}

            <span
              class="rounded-[50%] bg-white/20 p-2 transition-transform duration-200 group-hover:translate-x-1"
            >
              <UiIcon name="arrow-forward-rounded" />
            </span>
          </a>
        </UiButton>

        <div
          class="relative col-span-3 md:col-start-2 md:col-end-3 lg:hidden"
          :style="{
            order: Object.keys(tm('careers.section.how_we_work.cards')).length,
          }"
        >
          <UiImageWithOverlay
            :src="`${S3_ASSETS_URL}/careers/hero-03.jpg`"
            container-class="rounded-3xl overflow-clip after:!from-baltic-900/85 after:!to-baltic-900/85"
            class="aspect-square size-full object-cover"
          />

          <div class="absolute bottom-0 right-0 p-4">
            <UiButton as-child>
              <a
                href="/careers-listing"
                rel="nofollow noopener"
                class="group flex-col !items-end !gap-2 !rounded-[1.25rem] !p-6"
              >
                <span
                  class="inline-flex size-10 items-center justify-center rounded-[50%] bg-white/20 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                >
                  <UiIcon name="arrow-outward-rounded" />
                </span>

                {{ t("careers.section.how_we_work.action") }}
              </a>
            </UiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
